import { LOCATION_STATE_AU, STREET_TYPE_AU } from '@quipex/shared/contracts';

export const ADDRESS_MODE = {
    Street: 'street',
    City: 'city',
    Full: 'full',
} as const;
export type AddressMode = (typeof ADDRESS_MODE)[keyof typeof ADDRESS_MODE];
type InlineSubpremiseBoundaryKind =
    | 'completeSubpremise'
    | 'standaloneDelimiter';
type StreetNumberLeadingKind = 'letter' | 'numeric';
type StreetNumberSpan = { readonly end: number };
type InlineSubpremiseCandidate = {
    readonly boundaryKind: InlineSubpremiseBoundaryKind;
    readonly index: number;
    readonly leadingKind: StreetNumberLeadingKind;
    readonly streetNumberSpan: StreetNumberSpan;
};

const STREET_NUMBER_PREFIX_PATTERN = /^\d/u;
const STREET_NUMBER_LEADING_LETTER_PATTERN = /^\p{L}/u;
const STREET_NUMBER_TOKEN_PATTERN =
    /^(?=[a-z0-9/-]*\d)[a-z0-9]+(?:[/-][a-z0-9]+)*$/iu;
const CONNECTED_IDENTIFIER_CHUNK_PATTERN = /^[a-z0-9]+(?:[/-][a-z0-9]+)*$/iu;
const SINGLE_LETTER_IDENTIFIER_PATTERN = /^\p{L}$/u;
const ZERO_PADDED_NUMBER_PATTERN = /^0\d+$/u;
const ORDINAL_PATTERN = /^\d+(?:st|nd|rd|th)$/iu;
const FLOOR_QUALIFIERS = new Set(['basement', 'ground', 'lower', 'upper']);
const NAMED_SUBPREMISES = new Set(['the cottage', 'the lodge']);
const SINGLE_WORD_SUBPREMISES = new Set(['basement', 'penthouse']);
const STREET_TYPES = new Set([
    ...Object.entries(STREET_TYPE_AU).flatMap(([short, long]) => [
        short.toLowerCase(),
        long.toLowerCase(),
    ]),
    'esplanade',
]);
const STATE_SUFFIXES = LOCATION_STATE_AU.flatMap(({ code, name }) => [
    code.toLowerCase(),
    name.toLowerCase(),
]);
const STATE_DISPLAY_VALUES = new Map(
    LOCATION_STATE_AU.flatMap(({ code, name }) => [
        [code.toLowerCase(), code],
        [name.toLowerCase(), name],
    ])
);
const STREET_TYPE_DISPLAY_VALUES = new Map<string, string>([
    ...Object.entries(STREET_TYPE_AU).flatMap<[string, string]>(
        ([short, long]) => [
            [short.toLowerCase(), short],
            [long.toLowerCase(), toDisplayWords(long)],
        ]
    ),
]);
const SUBPREMISE_PREFIXES = new Set([
    'apartment',
    'building',
    'factory',
    'flat',
    'floor',
    'kiosk',
    'level',
    'lot',
    'office',
    'room',
    'shop',
    'site',
    'suite',
    'tenancy',
    'unit',
    'villa',
    'warehouse',
]);

export function isRecognisedStreetType(value: string): boolean {
    return STREET_TYPES.has(value.trim().toLowerCase());
}

export function toAddressFormat(
    value: unknown,
    mode: AddressMode = ADDRESS_MODE.Full
): string {
    if (typeof value === 'string') {
        const address = splitStringAddress(value);

        switch (mode) {
            case ADDRESS_MODE.Street: {
                return formatRecognisedStreet(address.street);
            }
            case ADDRESS_MODE.City: {
                return formatLocality(address.city);
            }
            case ADDRESS_MODE.Full:
            default: {
                return formatStringAddress(value, address);
            }
        }
    }

    if (!value || !isRecord(value)) return '';

    const {
        unitNumber,
        streetNumber,
        streetName,
        city,
        state,
        postcode,
        address,
        buildingName,
        buildingAddress,
        buildingCity,
    } = getValuesFromUnknownRecord(value);

    switch (mode) {
        case ADDRESS_MODE.Street: {
            const hasStreetFromKeys = hasAddressKeys({
                mode: ADDRESS_MODE.Street,
                streetNumber,
                streetName,
            });

            const sanitisedStreetAddress = toAddressFormat(
                buildingAddress,
                ADDRESS_MODE.Street
            );
            const sanitisedBuildingName = toAddressFormat(
                buildingName,
                ADDRESS_MODE.Street
            );
            const streetAddressFromFullAddress = toAddressFormat(
                address,
                ADDRESS_MODE.Street
            );

            return (
                (hasStreetFromKeys &&
                    formatStructuredStreet({
                        unitNumber,
                        streetNumber,
                        streetName,
                    })) ||
                sanitisedStreetAddress ||
                sanitisedBuildingName ||
                streetAddressFromFullAddress ||
                ''
            );
        }
        case ADDRESS_MODE.City: {
            const hasCityFromKeys = hasAddressKeys({
                mode: ADDRESS_MODE.City,
                city,
                state,
                postcode,
            });

            const cityFromFullAddress = formatLocality(
                address.split(',').slice(1).join(' ')
            );

            const sanitisedCityLocation = formatLocality(buildingCity);

            return (
                (hasCityFromKeys &&
                    formatLocality(`${city} ${state} ${postcode}`)) ||
                cityFromFullAddress ||
                sanitisedCityLocation ||
                ''
            );
        }

        case ADDRESS_MODE.Full:
        default: {
            const hasAddressFromKeys = hasAddressKeys({
                mode: ADDRESS_MODE.Full,
                streetNumber,
                streetName,
                city,
                state,
                postcode,
            });

            const buildingAddressAndCity = prepareFullAddressFromAddressCity({
                buildingAddress,
                buildingCity,
            });

            return (
                (hasAddressFromKeys &&
                    `${formatStructuredStreet({ unitNumber, streetNumber, streetName })}, ${formatStructuredLocality({ city, state, postcode })}`) ||
                toAddressFormat(address, ADDRESS_MODE.Full) ||
                buildingAddressAndCity ||
                toAddressFormat(buildingName, ADDRESS_MODE.Street) ||
                toAddressFormat(buildingAddress, ADDRESS_MODE.Full) ||
                ''
            );
        }
    }
}

function formatStringAddress(
    original: string,
    address: { street: string; city: string }
): string {
    const street = formatRecognisedStreet(address.street);
    const city = formatLocality(address.city);
    const streetRecognised =
        street !== address.street || isKnownStreet(address.street);
    const cityRecognised =
        city !== address.city || isKnownLocality(address.city);

    if (streetRecognised && cityRecognised && city) {
        return `${street}, ${city}`;
    }

    if (streetRecognised && !address.city) return street;

    return original;
}

function splitStringAddress(value: string): { street: string; city: string } {
    const parts = value.split(',').map((part) => part.trim());
    const firstPart = parts[0] ?? '';
    const secondPart = parts[1] ?? '';
    const hasExplicitSubpremise = isExplicitSubpremise(firstPart);
    const hasSpacedBareSubpremise = isSpacedBareSubpremise(firstPart);
    const hasNamedSubpremise =
        !STREET_NUMBER_PREFIX_PATTERN.test(firstPart) &&
        !isStreetAddress(firstPart);
    const hasSeparateSubpremise =
        parts.length >= 2 &&
        ((hasExplicitSubpremise && isStreetAddress(secondPart, true)) ||
            (hasSpacedBareSubpremise && isNumberedStreetAddress(secondPart)) ||
            (hasNamedSubpremise && isNumberedStreetAddress(secondPart)));
    const street = hasSeparateSubpremise
        ? `${firstPart}, ${secondPart}`
        : normaliseInlineSubpremiseAddress(firstPart);
    const cityParts = parts.slice(hasSeparateSubpremise ? 2 : 1);

    return {
        street,
        city: formatLocality(cityParts.join(' ')),
    };
}

function isStreetAddress(value: string, allowUnknownType = false): boolean {
    const parts = value.trim().split(/\s+/u);
    const streetType = (parts.at(-1) ?? '')
        .toLowerCase()
        .replace(/\p{P}+$/gu, '');

    return (
        parts.length >= 2 &&
        (isRecognisedStreetType(streetType) ||
            isNumberedStreetAddress(value) ||
            (allowUnknownType && !hasStateSuffix(parts)))
    );
}

function isNumberedStreetAddress(value: string): boolean {
    const parts = value.trim().split(/\s+/u);
    const hasPostcode = /^\d{4}$/u.test(parts.at(-1) ?? '');

    return (
        parts.length >= 2 &&
        STREET_NUMBER_TOKEN_PATTERN.test(parts[0] ?? '') &&
        !(hasPostcode && hasStateSuffix(parts))
    );
}

function hasStateSuffix(parts: string[]): boolean {
    const lastPart = parts.at(-1) ?? '';
    const partsWithoutPostcode = /^\d{4}$/u.test(lastPart)
        ? parts.slice(0, -1)
        : parts;
    const value = partsWithoutPostcode.join(' ').toLowerCase();

    return STATE_SUFFIXES.some(
        (state) => value === state || value.endsWith(` ${state}`)
    );
}

function isExplicitSubpremise(value: string): boolean {
    const parts = value.trim().split(/\s+/u);
    const firstPart = parts[0]?.toLowerCase() ?? '';
    const lastPart = parts.at(-1)?.toLowerCase() ?? '';
    const normalisedValue = parts.join(' ').toLowerCase();
    const isBareUnitIdentifier =
        parts.length === 1 && STREET_NUMBER_TOKEN_PATTERN.test(firstPart);

    return (
        isBareUnitIdentifier ||
        NAMED_SUBPREMISES.has(normalisedValue) ||
        SINGLE_WORD_SUBPREMISES.has(normalisedValue) ||
        (parts.length >= 2 &&
            SUBPREMISE_PREFIXES.has(firstPart) &&
            !isStreetAddress(value)) ||
        (lastPart === 'floor' &&
            (ORDINAL_PATTERN.test(firstPart) ||
                FLOOR_QUALIFIERS.has(firstPart)))
    );
}

function isSpacedBareSubpremise(value: string): boolean {
    const parts = value.trim().split(/\s+/u);
    const isIdentifierToken = (part: string): boolean =>
        STREET_NUMBER_TOKEN_PATTERN.test(part) || part === '/' || part === '-';

    return (
        parts.length >= 2 &&
        STREET_NUMBER_TOKEN_PATTERN.test(parts[0] ?? '') &&
        STREET_NUMBER_TOKEN_PATTERN.test(parts.at(-1) ?? '') &&
        parts.every(isIdentifierToken)
    );
}

function normaliseInlineSubpremiseAddress(value: string): string {
    const parts = value.trim().split(/\s+/u);
    const prefix = parts[0]?.toLowerCase() ?? '';
    if (!SUBPREMISE_PREFIXES.has(prefix)) return value;

    const streetNumberCandidates: InlineSubpremiseCandidate[] = parts
        .map((_, index) => index)
        .flatMap((index) => {
            if (index < 2) return [];

            const streetNumberSpan = findStreetNumberSpan(parts, index);
            const boundaryKind = getInlineSubpremiseBoundaryKind(parts, index);
            const leadingKind: StreetNumberLeadingKind =
                STREET_NUMBER_LEADING_LETTER_PATTERN.test(parts[index] ?? '')
                    ? 'letter'
                    : 'numeric';
            return streetNumberSpan &&
                streetNumberSpan.end < parts.length &&
                boundaryKind &&
                isKnownStreet(parts.slice(index).join(' '))
                ? [{ boundaryKind, index, leadingKind, streetNumberSpan }]
                : [];
        });
    const completeCandidates = streetNumberCandidates.filter(
        ({ boundaryKind }) => boundaryKind === 'completeSubpremise'
    );
    const hasCompleteCandidates = completeCandidates.length > 0;
    const preferredCandidates = hasCompleteCandidates
        ? completeCandidates
        : streetNumberCandidates;
    const firstPreferredCandidate = preferredCandidates[0];
    const secondPreferredCandidate = preferredCandidates[1];
    const firstLetterLedStandaloneCandidate = streetNumberCandidates.find(
        ({ boundaryKind, index, leadingKind, streetNumberSpan }) =>
            boundaryKind === 'standaloneDelimiter' &&
            leadingKind === 'letter' &&
            streetNumberSpan.end > index + 1
    );
    let streetIndex = hasCompleteCandidates
        ? firstPreferredCandidate?.index
        : firstLetterLedStandaloneCandidate
          ? firstLetterLedStandaloneCandidate.index
          : preferredCandidates.at(-1)?.index;

    if (
        firstPreferredCandidate &&
        SINGLE_LETTER_IDENTIFIER_PATTERN.test(
            parts[firstPreferredCandidate.index - 1] ?? ''
        ) &&
        ZERO_PADDED_NUMBER_PATTERN.test(
            parts[firstPreferredCandidate.index] ?? ''
        ) &&
        secondPreferredCandidate?.index === firstPreferredCandidate.index + 1
    ) {
        streetIndex = secondPreferredCandidate.index;
    }

    if (streetIndex === undefined) return value;

    const unitParts = parts.slice(0, streetIndex);
    if (unitParts.at(-1) === '/' || unitParts.at(-1) === '-') unitParts.pop();

    const unit = unitParts.join(' ');
    const street = parts.slice(streetIndex).join(' ');

    return `${unit}, ${street}`;
}

function findStreetNumberSpan(
    parts: string[],
    start: number
): StreetNumberSpan | undefined {
    const firstPart = parts[start] ?? '';
    const isFirstPartChunk = CONNECTED_IDENTIFIER_CHUNK_PATTERN.test(firstPart);
    if (!isFirstPartChunk) return undefined;

    let end = start + 1;
    while (
        (parts[end] === '/' || parts[end] === '-') &&
        CONNECTED_IDENTIFIER_CHUNK_PATTERN.test(parts[end + 1] ?? '')
    ) {
        end += 2;
    }

    const connectedValue = parts.slice(start, end).join('');
    return STREET_NUMBER_TOKEN_PATTERN.test(connectedValue)
        ? { end }
        : undefined;
}

function isValidSubpremiseIdentifierSpan(parts: string[]): boolean {
    if (parts.length === 0) return false;

    return parts.every((part, index) => {
        const isSeparator = part === '/' || part === '-';
        if (!isSeparator) return CONNECTED_IDENTIFIER_CHUNK_PATTERN.test(part);

        const previousPart = parts[index - 1] ?? '';
        const nextPart = parts[index + 1] ?? '';
        return (
            CONNECTED_IDENTIFIER_CHUNK_PATTERN.test(previousPart) &&
            CONNECTED_IDENTIFIER_CHUNK_PATTERN.test(nextPart)
        );
    });
}

function getInlineSubpremiseBoundaryKind(
    parts: string[],
    streetIndex: number
): InlineSubpremiseBoundaryKind | undefined {
    const identifiers = parts.slice(1, streetIndex);
    if (isValidSubpremiseIdentifierSpan(identifiers)) {
        return 'completeSubpremise';
    }

    const trailingToken = identifiers.at(-1);
    const hasTrailingDelimiter = trailingToken === '/' || trailingToken === '-';

    return hasTrailingDelimiter &&
        isValidSubpremiseIdentifierSpan(identifiers.slice(0, -1))
        ? 'standaloneDelimiter'
        : undefined;
}

function formatLocality(value: string): string {
    const locality = value.replace(/,/gu, ' ').trim().replace(/\s+/gu, ' ');
    if (!locality) return '';

    const parts = locality.split(' ');
    const lastPart = parts.at(-1) ?? '';
    const hasPostcode = /^\d{4}$/u.test(lastPart);
    const localityWithoutPostcode = parts.slice(
        0,
        hasPostcode ? -1 : undefined
    );
    const stateMatch = findStateSuffix(localityWithoutPostcode);

    if (!stateMatch || stateMatch.index < 1) return value.trim();

    const city = toDisplayWords(
        localityWithoutPostcode.slice(0, stateMatch.index).join(' ')
    );
    const postcode = hasPostcode ? ` ${lastPart}` : '';

    return `${city}, ${stateMatch.display}${postcode}`;
}

function isKnownLocality(value: string): boolean {
    const parts = value.replace(/,/gu, ' ').trim().split(/\s+/u);
    const hasPostcode = /^\d{4}$/u.test(parts.at(-1) ?? '');
    const withoutPostcode = parts.slice(0, hasPostcode ? -1 : undefined);
    const stateMatch = findStateSuffix(withoutPostcode);

    return Boolean(stateMatch && stateMatch.index >= 1);
}

function findStateSuffix(
    parts: string[]
): { display: string; index: number } | undefined {
    for (const wordCount of [3, 2, 1]) {
        const index = parts.length - wordCount;
        if (index < 0) continue;

        const key = parts.slice(index).join(' ').toLowerCase();
        const display = STATE_DISPLAY_VALUES.get(key);
        if (display) return { display, index };
    }

    return undefined;
}

function hasAddressKeys({
    mode,
    streetNumber,
    streetName,
    city,
    state,
    postcode,
}: {
    mode: AddressMode;
    streetNumber?: string;
    streetName?: string;
    city?: string;
    state?: string;
    postcode?: string;
}) {
    switch (mode) {
        case ADDRESS_MODE.Street: {
            return streetNumber && streetName;
        }

        case ADDRESS_MODE.City: {
            return city && state && postcode;
        }

        case ADDRESS_MODE.Full:
        default: {
            return streetNumber && streetName && city && state && postcode;
        }
    }
}

function prepareFullAddressFromAddressCity({
    buildingAddress,
    buildingCity,
}: {
    buildingAddress: string;
    buildingCity: string;
}) {
    if (!buildingAddress || !buildingCity) return '';

    const sanitisedStreetAddress = toAddressFormat(
        buildingAddress,
        ADDRESS_MODE.Street
    );
    const sanitisedCity = formatLocality(buildingCity);

    return sanitisedStreetAddress && sanitisedCity
        ? `${sanitisedStreetAddress}, ${sanitisedCity}`
        : '';
}

function getValuesFromUnknownRecord(value: Record<string, unknown>): {
    unitNumber: string;
    streetNumber: string;
    streetName: string;
    city: string;
    state: string;
    postcode: string;
    /** full */
    address: string;
    /** streetNumber and streetName */
    buildingName: string;
    /** streetNumber and streetName OR full */
    buildingAddress: string;
    /** city, state and postcode */
    buildingCity: string;
} {
    /** full */
    const address =
        typeof value['address'] === 'string' ? value['address'] : '';

    const unitNumber =
        typeof value['unitNumber'] === 'string' ? value['unitNumber'] : '';
    const streetNumber =
        typeof value['streetNumber'] === 'string' ? value['streetNumber'] : '';
    const streetName =
        typeof value['streetName'] === 'string' ? value['streetName'] : '';
    const city = typeof value['city'] === 'string' ? value['city'] : '';
    const state = typeof value['state'] === 'string' ? value['state'] : '';
    const postcode =
        typeof value['postcode'] === 'number'
            ? value['postcode'].toString()
            : typeof value['postcode'] === 'string'
              ? value['postcode']
              : '';

    /** streetNumber and streetName */
    const buildingName =
        typeof value['buildingName'] === 'string' ? value['buildingName'] : '';

    /** streetNumber and streetName */
    const buildingAddress =
        typeof value['buildingAddress'] === 'string'
            ? value['buildingAddress']
            : '';

    /** city, state and postcode */
    const buildingCity =
        typeof value['buildingCity'] === 'string' ? value['buildingCity'] : '';

    return {
        unitNumber,
        streetNumber,
        streetName,
        city,
        state,
        postcode,
        address,
        buildingName,
        buildingAddress,
        buildingCity,
    };
}

function toStreetAddress({
    unitNumber,
    streetNumber,
    streetName,
}: {
    unitNumber?: string;
    streetNumber: string;
    streetName: string;
}): string {
    const trimmedUnitNumber = unitNumber?.trim();
    const streetAddress = `${streetNumber} ${streetName}`;
    return trimmedUnitNumber
        ? `Unit ${trimmedUnitNumber}, ${streetAddress}`
        : streetAddress;
}

function formatStructuredStreet({
    unitNumber,
    streetNumber,
    streetName,
}: {
    unitNumber?: string;
    streetNumber: string;
    streetName: string;
}): string {
    return toStreetAddress({
        unitNumber,
        streetNumber: streetNumber.trim(),
        streetName: formatStreetName(streetName),
    });
}

function formatStructuredLocality({
    city,
    state,
    postcode,
}: {
    city: string;
    state: string;
    postcode: string;
}): string {
    const displayState = STATE_DISPLAY_VALUES.get(state.trim().toLowerCase());
    const cityDisplay = toDisplayWords(city.trim());
    const stateDisplay = displayState ?? state.trim();

    return `${cityDisplay}, ${stateDisplay} ${postcode.trim()}`;
}

function formatRecognisedStreet(value: string): string {
    const segments = value.split(',').map((segment) => segment.trim());

    if (
        segments.length === 2 &&
        (isExplicitSubpremise(segments[0] ?? '') ||
            isSpacedBareSubpremise(segments[0] ?? ''))
    ) {
        const street = segments[1] ?? '';
        return isKnownStreet(street)
            ? `${formatSubpremise(segments[0] ?? '')}, ${formatStreetName(street)}`
            : value;
    }

    if (segments.length !== 1) return value;

    const segment = segments[0] ?? '';
    if (isPostalBox(segment)) return formatPostalBox(segment);
    if (!isKnownStreet(segment)) return value;

    return formatStreetName(segment);
}

function isKnownStreet(value: string): boolean {
    const streetType = value
        .trim()
        .split(/\s+/u)
        .at(-1)
        ?.toLowerCase()
        .replace(/\p{P}+$/gu, '');

    return Boolean(
        (streetType && isRecognisedStreetType(streetType)) || isPostalBox(value)
    );
}

function formatStreetName(value: string): string {
    const trimmedValue = value.trim();
    const tokens = trimmedValue.split(/\s+/u);
    const streetTypeIndex = tokens.length - 1;
    const streetTypeToken = tokens[streetTypeIndex] ?? '';
    const streetTypeMatch = streetTypeToken.match(/^(.*?)([\p{L}]+)(\p{P}*)$/u);
    const streetType = streetTypeMatch?.[2].toLowerCase() ?? '';
    const streetTypeDisplay = STREET_TYPE_DISPLAY_VALUES.get(streetType);
    let lexicalStart = 0;
    const streetNumberSpan = findStreetNumberSpan(tokens, 0);

    if (streetNumberSpan) {
        if (streetNumberSpan.end === 1) {
            tokens[0] = formatStreetNumber(tokens[0] ?? '');
        }
        lexicalStart = streetNumberSpan.end;
    }

    for (let index = lexicalStart; index < tokens.length; index += 1) {
        tokens[index] = toDisplayWords(tokens[index] ?? '');
    }

    if (streetTypeDisplay && streetTypeMatch) {
        tokens[streetTypeIndex] =
            `${streetTypeMatch[1]}${streetTypeDisplay}${streetTypeMatch[3]}`;
    }

    return tokens.join(' ');
}

function formatStreetNumber(value: string): string {
    return ORDINAL_PATTERN.test(value) ? value.toLowerCase() : value;
}

function formatSubpremise(value: string): string {
    const trimmedValue = value.trim();
    const parts = trimmedValue.split(/\s+/u);
    const prefix = parts[0]?.toLowerCase() ?? '';

    if (SUBPREMISE_PREFIXES.has(prefix)) {
        return [
            toDisplayWords(parts[0] ?? ''),
            ...parts.slice(1).map(formatSubpremiseIdentifier),
        ].join(' ');
    }

    return toDisplayWords(trimmedValue);
}

function formatSubpremiseIdentifier(value: string): string {
    if (/^\d/u.test(value)) return value;

    const hasUppercaseIdentifier =
        /\p{L}/u.test(value) && value === value.toUpperCase();

    return hasUppercaseIdentifier ? value : toDisplayWords(value);
}

function isPostalBox(value: string): boolean {
    return /^(?:G?PO)\s+BOX\s+\S+$/iu.test(value.trim());
}

function formatPostalBox(value: string): string {
    const [, prefix = '', identifier = ''] =
        value.trim().match(/^(G?PO)\s+BOX\s+(\S+)$/iu) ?? [];

    return `${prefix.toUpperCase()} Box ${identifier}`;
}

function toDisplayWords(value: string): string {
    const valueWithOrdinals = value.replace(/\d+(?:st|nd|rd|th)\b/giu, (word) =>
        word.toLowerCase()
    );

    return valueWithOrdinals.replace(
        /[\p{L}\p{M}]+(?:['’][\p{L}\p{M}]+)*/gu,
        (word, offset: number) => {
            const isOrdinalSuffix =
                offset > 0 &&
                /\d$/u.test(valueWithOrdinals.slice(0, offset)) &&
                /^(?:st|nd|rd|th)$/iu.test(word);
            if (isOrdinalSuffix) return word.toLowerCase();

            if (word !== word.toUpperCase() && word !== word.toLowerCase()) {
                return word;
            }

            const normalised =
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

            return normalised.replace(
                /^(\p{L})(['’])(\p{L})/u,
                (_, first: string, apostrophe: string, following: string) =>
                    `${first}${apostrophe}${following.toUpperCase()}`
            );
        }
    );
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && !Array.isArray(value) && value != null;
}
